import { axios } from "@/libraries/axios";
import type {
	GetDeleteMessageBodySchema,
	GetMessageDiscussionsSchema,
	PatchUpdateMessageBodySchema,
	PostCreateMessageBodySchema,
	PostReadMessagePayloadSchema,
	SyncMessageParamsSchema,
} from "@/schemas";
import { CASE_TABS_VALUE, MESSAGE_TAG } from "@/schemas";
import type {
	CreateMessageResponse,
	DeletedMessageResponse,
	GetMessageDiscussionsResponse,
	GetMessageResponse,
	GetMessagesSyncResponse,
	UpdateMessageResponse,
} from "@/types";
import type { GetMessageTotalUnreadResponseType } from "@/types/get-message-total-unread";
import type { AxiosRequestConfig } from "axios";
class MessageService {
	async discussions(params: GetMessageDiscussionsSchema) {
		const apiUrl = new URL(
			"/api/v1/messages/discussions",
			axios.defaults.baseURL,
		);
		try {
			const baseParams = {
				page: params.page || 1,
				limit: params.limit || 25,
			};
			if ((params.tags as any) === CASE_TABS_VALUE.ALL) params.tags = undefined;

			const isFiles =
				params.tags &&
				[
					MESSAGE_TAG.FILES,
					MESSAGE_TAG.IMAGING,
					MESSAGE_TAG.PHOTOS,
					MESSAGE_TAG.RESULTS,
					MESSAGE_TAG.OTHER,
					MESSAGE_TAG.MEDIA,
				].includes(params.tags as any);
			if (isFiles) {
				if (params.tags === MESSAGE_TAG.FILES) params.tags = undefined;
				apiUrl.pathname = "/api/v1/documents";
			}

			const res = await axios.get<GetMessageDiscussionsResponse>(
				apiUrl.pathname,
				{
					params: {
						...baseParams,
						...params,
					},
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async create(message: PostCreateMessageBodySchema) {
		try {
			const res = await axios.post<CreateMessageResponse>("/api/v1/messages", {
				message,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async update(payload: PatchUpdateMessageBodySchema) {
		try {
			const { id, ...message } = payload;
			if (message.channelName) {
				// @ts-expect-error
				message.channelName = undefined;
			}
			const res = await axios.patch<UpdateMessageResponse>(
				`/api/v1/messages/${id}`,
				{
					message,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async delete(payload: GetDeleteMessageBodySchema) {
		try {
			const { id } = payload;
			const res = await axios.get<DeletedMessageResponse>(
				`/api/v1/messages/${id}/delete`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async read(
		payload: PostReadMessagePayloadSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			const res = await axios.post("/api/v1/messages/read", payload, config);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sync(params: SyncMessageParamsSchema) {
		try {
			const res = await axios.get<GetMessagesSyncResponse>(
				"/api/v1/messages/sync",
				{
					params,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async detail(id: number) {
		try {
			const res = await axios.get<GetMessageResponse>(`/api/v1/messages/${id}`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async unread() {
		try {
			const res = await axios.get<GetMessageTotalUnreadResponseType>(
				"/api/v1/messages/total_unreads",
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async accept(messageId: number) {
		try {
			const res = await axios.post(`/api/v1/messages/${messageId}/accept`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async decline(messageId: number) {
		try {
			const res = await axios.post(`/api/v1/messages/${messageId}/decline`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}
}

export const messageService = Object.freeze(new MessageService());
